import {Box, MsgOver, OSkinableInit} from "back/commons/basis";
import {IReg, IRegPointer, IUiEnv, REG} from "lib/commons/registry";
import {DOM, JSX, ShadowJsx} from "lib/commons/xml/dom";
import {DOMSH} from "lib/commons/xml/domsh";
import {GridCol, GridColDef, IGridLineDrawer} from "back/commons/widgets/grid-core";
import {ICoreUniverseEnv} from "lib/core/universe";
import {JUser, UsersSrv} from "lib/core/user";
import {AccelKeyMgr, ACTION, Action, ActionWrapper, IAction} from "lib/commons/actions";
import {CellBuilderFlagIcon, CellBuilderIconLabel, GridDataRowJson} from "back/commons/widgets/grid-libs";
import {BaseAreaViewAsync, IView} from "lib/commons/views";
import "back/core/dialogs/dialogs_Perms";
import {GridColTreeDef, GridDataHolderJsonTree} from "back/commons/widgets/tree";
import {Grid, OGridSmallInit} from "back/commons/widgets/grid-tags";
import {IContextMenuActionsPointer} from "back/commons/actionables";
import {BarActions, OBarActionsInit} from "back/commons/widgets/bars";
import {OTabsInit, Tabs} from "back/commons/widgets/tabs";
import {IArea} from "lib/commons/areas";
import {IRoleEditUiHandlerContext, RoleEditUiHandler, ROLES} from "lib/commons/roles";
import {InputUserPanel, OInputUserInit} from "back/core/widgets/inputs";
import {LANG} from "lib/commons/lang";
import {OUserRefInit, UserRef} from "back/core/widgets/userRef";
import {ActionBtn, Button} from "back/commons/widgets/buttons";


export interface RoleUiHandlerContextUsed {
	whiteListLevelsUid?: string[];
}

export interface ORolesExplorerInit extends OSkinableInit {
	reg: IReg<ICoreUniverseEnv>
	gridInit?: OGridSmallInit
	uiContext?: "popup" | undefined
}

export class RolesExplorer<E extends IRegPointer<ICoreUniverseEnv & IUiEnv>> extends BaseAreaViewAsync<E> implements IRegPointer<ICoreUniverseEnv & IUiEnv> {
	reg: IReg<ICoreUniverseEnv & IUiEnv>;

	protected views: IView;

	protected async _initialize(init: ORolesExplorerInit): Promise<void> {
		if (!this.reg) this.reg = REG.createSubReg(this.findReg(init));

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);

		const areas = (this.reg.mergeLists("rolesExplorer:tabs") as IArea<IRegPointer<ICoreUniverseEnv & IUiEnv>>[]).filter(entry => {return entry.isVisible(this)});
		const areasContext = {reg: this.reg};

		if (areas.length > 1) {
			this.views = sr.appendChild(<Tabs id="tabs" î={{
				reg: this.reg,
				areasContext: areasContext,
				areas,
				skinOver: "rolesExplorer/tabs"
			} as OTabsInit<E>}/>) as Tabs<E>;
		} else
			this.views = sr.appendChild(await areas[0].loadBody(areasContext));

	}
}

customElements.define("c-rolesexplorer", RolesExplorer);

REG.reg.registerSkin('c-rolesexplorer', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-width: 0;
		min-height: 0;
		flex-direction: column;
		overflow: hidden;
	}

	c-tabs {
		flex: 1;
	}
`);

export interface JRoleExpEntry {
	/** Identifiant unique du StructLevel (fullUri, ...) */
	uid: string
}

export interface JRoleExpEntrySetAccount extends JRoleExpEntry {
	account: string
	allowed?: string[]
	denied?: string[]
	userUnknown?: true | undefined
}

export interface JRoleExpEntryStructLevel<R extends JRoleExpEntryStructLevel<R> | JRoleExpEntrySetAccount> extends JRoleExpEntry {
	structLevel: 'roles' | string
	title: string
	ch?: R[]
}


/**
 * Contexte des actions associées à une affectation de rôles {user,entry}
 */
export interface IUsersRolesActionCtx<E extends IRegPointer<ICoreUniverseEnv & IUiEnv>, R extends (JRoleExpEntryStructLevel<R> | JRoleExpEntrySetAccount), CTX extends IUsersRolesActionCtx<E, R, CTX>> extends IRegPointer<ICoreUniverseEnv> {
	rolesExplorer: RolesExplorerTabBase<E, R, CTX>
}

export interface ORolesExplorerTabInit extends OSkinableInit {
	reg: IReg<ICoreUniverseEnv>
	gridInit?: OGridSmallInit
	uiContext?: "popup" | undefined
	/** Filtre TXT : valeur de recherche initiale */
	filterTxt?: string
	filterTxtPlaceholder?: string
	/** Filtre account : valeur de recherche initiale */
	filterAccounts?: string[]
	filterAccountsInputInit: OInputUserInit<any>
}

export abstract class RolesExplorerTabBase<E extends IRegPointer<ICoreUniverseEnv & IUiEnv>, R extends (JRoleExpEntryStructLevel<R> | JRoleExpEntrySetAccount), CTX extends IUsersRolesActionCtx<E, R, CTX>> extends BaseAreaViewAsync<E> implements IGridLineDrawer {
	reg: IReg<ICoreUniverseEnv & IUiEnv>;

	protected params: ORolesExplorerTabInit;

	// Filtres
	protected filterTxt: HTMLInputElement;

	protected filterAccounts: InputUserPanel<any>;

	protected usersSrv: UsersSrv;

	dataHolder: GridDataHolderJsonTree<R>;

	protected toolbarTop: BarActions<CTX>;

	grid: Grid & IContextMenuActionsPointer<E>;

	protected _detailsBoxElt: HTMLDivElement;

	protected filterFormElt: HTMLFormElement;

	protected razFilterBtn: ActionBtn<IUsersRolesActionCtx<E, R, CTX>>;

	protected async _initialize(init: ORolesExplorerTabInit): Promise<void> {
		this.params = init;
		if (!this.reg) this.reg = REG.createSubReg(this.findReg(init));
		this.usersSrv = this.reg.env.universe.adminUsers;

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg.installSkin("webzone:panel", sr);
		this.reg.installSkin("form-control-areas", sr);
		this.reg.installSkin("rolesexplorer-core", sr);
		this.reg.installSkin("c-rolesexplorer-core-widgets", sr);
		this._initAndInstallSkin(this.localName, init);

		let contentElt = sr.appendChild(<div id="content" c-resizable/>) as HTMLDivElement;

		let gridContentElt = contentElt.appendChild(<div id="gridContent"/>) as HTMLDivElement;

		// - Filtres
		let headElt = gridContentElt.appendChild(<div id="head"/>) as HTMLDivElement;
		this.filterFormElt = headElt.appendChild(<form id="filter">
		</form>) as HTMLFormElement;

		this.filterTxt = this.filterFormElt.appendChild(<input type="search" id="filterTxt" name="filterTxt" spellcheck="false" autocomplete="off" placeholder={this.params.filterTxtPlaceholder || "Filtrer..."} oninput={this.onApplyFilter}
																													 onkeydown={this.onKeyPress}/>) as HTMLInputElement;
		this.filterTxt.focus();

		this.filterAccounts = this.filterFormElt.appendChild(new InputUserPanel().initialize(Object.assign({
			name: "filterAccounts",
			reg: this.reg,
			emptySelectionMsg: "Utilisateurs/groupes...",
			userCard: "single",
			usersGridInit: {
				usersSrv: this.usersSrv,
				filterType: null,
				filterTypeInputVisibility: true,
				normalizeList: (array, filterTxtRegExp) => {
					this._lastUnknownUsers?.forEach(unknownUser => {
						if ((!filterTxtRegExp || unknownUser.account.match(filterTxtRegExp)) && !array.find(entry => entry.account === unknownUser.account))
							array.push(unknownUser);
						array.sort((a, b) => a.account.localeCompare(b.account));
					});
					return array;
				}
			},
		} as OInputUserInit<E>, this.params.filterAccountsInputInit))) as InputUserPanel<E>;
		await this.filterAccounts.initializedAsync;
		DOM.addClass(this.filterAccounts, "formElt");
		this.filterAccounts.addEventListener("change", async (evt) => {
			this.onApplyFilter.call(this.filterAccounts, evt);
		});
		this.filterAccounts.title = "Filtre utilisateurs/groupes";

		this.razFilterBtn = this.filterFormElt.appendChild(ActionBtn.buildButton(actionRazFilters, this.actionContext, "bar")) as ActionBtn<IUsersRolesActionCtx<E, R, CTX>>;

		// - Toolbar
		this.reg.addToList("actions:rolesexplorer:show:toolbar:top", "actionRefreshUi", 1, actionRefreshUi);

		let toolbarsBox = contentElt.appendChild(<div id="toolbarsBox"/>);
		this.toolbarTop = toolbarsBox.appendChild(new BarActions<CTX>().initialize({
			reg: this.reg,
			actions: ACTION.injectSepByGroup(
				this.reg.mergeLists("actions:rolesexplorer", "actions:rolesexplorer:show:toolbar:top", "actions:rolesexplorer:show:toolbar:top:" + this.id, "actions:rolesexplorer:" + this.id),
				this.reg.getPref("actions.rolesexplorer.groupOrder", "refresh *"), this.actionContext) as IAction<CTX>[],
			uiContext: "bar",
			actionContext: this.actionContext,
			disableFullOverlay: true,
		} as OBarActionsInit<CTX>)) as BarActions<CTX>;
		this.toolbarTop.setAttribute("class", "showToolbarTop");

		// - Tree
		this.dataHolder = new GridDataHolderJsonTree<R>('ch');

		let accelKeyMgr = new AccelKeyMgr().initFromMapActions(this.reg.mergeListsAsMap("accelkeys:rolesexplorer", "accelkeys:rolesexplorer:" + this.id));
		accelKeyMgr.initFromMapActions(this.reg.mergeListsAsMap("accelkeys:rolesexplorer", "accelkeys:rolesexplorer:" + this.id));

		this.grid = gridContentElt.appendChild(new Grid().initialize(Object.assign(init.gridInit || {}, {
			selType: "mono",
			hideHeaders: false,
			columnDefs: this.colsDefBase(),
			skinOver: `rolesexplorer-core-tree c-rolesexplorer-core-widgets ${this.localName}-tree`,
			skinScroll: "scroll/small",
			lineDrawer: this,
			autoSelOnFocus: 'first',
			dataHolder: this.dataHolder,
			hideSortBtns: true,
			noResizableCol: true,
			emptyBody: () => <c-msg>Tous les rôles affectés ont été filtrés</c-msg>,
			//defaultAction: init.defaultAction,
			//defaultActionCtx: this,
		} as OGridSmallInit)));

		this.grid.addEventListener('grid-select', async (ev: Event) => {
			this.toolbarTop.refreshContent();
			await this.showDetails(this.dataHolder.getSelectedDatas());
		});

		this.grid.uiEvents.on('rowDblclick', (row: GridDataRowJson<R> | null, ev: MouseEvent) => {
			if ((row?.rowDatas as JRoleExpEntrySetAccount).account) {
				this.filterAccounts.value = [(row.rowDatas as JRoleExpEntrySetAccount).account];
				this.refreshUi();
			}
		});

		new MutationObserver((entries) => {
			for (const entry of entries) {
				if (entry.type == "attributes" && entry.attributeName == "data-roles-disabled") {
					this.refreshUi();
				}
			}
		}).observe(this.grid, {attributes: true});

		// - Details
		sr.appendChild(<c-resizer/>);
		this._detailsBoxElt = sr.appendChild(<div id="details" c-resizable hidden/>) as HTMLDivElement;

		this.razFilters();
		return this.refresh();
	}

	get actionContext(): IUsersRolesActionCtx<E, R, CTX> {
		return {
			rolesExplorer: this,
		}
	}

	async _refresh() {
		this.refreshFreeze(1);
		try {
			super._refresh();
			await this.refreshUi(true);
		} finally {
			this.refreshFreeze(-1);
		}
	}

	private _lastFetchDatas: R[];

	private _lastUnknownUsers: Set<JUser>;

	/** Refresh UI */
	private async refreshUi(withFetchDatas = false) {
		const selectedRows = this.dataHolder.getSelectedDatas();

		if (withFetchDatas || !this._lastFetchDatas) {
			this._lastFetchDatas = await new MsgOver().setCustomMsg("Veuillez patienter...", "info").showMsgOver(this).waitFor(
				this.doFetchDatas()
			);
			Object.freeze(this._lastFetchDatas);
			this._lastUnknownUsers = new Set<JUser>();

			// Re-calcul des colonnes de rôles
			const roles: Set<string> = new Set<string>();

			const findRolesIn = (level: R[]) => {
				if (level) {
					level.forEach((entry) => {
						(entry as JRoleExpEntrySetAccount).denied?.forEach(role => roles.add(role));
						(entry as JRoleExpEntrySetAccount).allowed?.forEach(role => roles.add(role));
						if ((entry as JRoleExpEntrySetAccount).userUnknown) this._lastUnknownUsers.add({account: (entry as JRoleExpEntrySetAccount).account, isUnknown: true});
						findRolesIn((entry as JRoleExpEntryStructLevel<R>).ch);
					});
				}
			}

			findRolesIn(this._lastFetchDatas);
			this.dataHolder.setDatas([]);//Optimisation pour ne pas forcer le grid à se recalculer avec les datas actuelles qui seront actualisées dans qq lignes ?
			await this.updateRolesColsDef(roles);

			// Actualisation de la liste des users inconnus
			//TODO this.filterAccounts.forcedIniUsers = Array.from(unknownUsers);
		}

		// # Application des filtres
		const filterTxt = this.filterTxt.value;
		const filterTxtPattern = filterTxt ? new RegExp(LANG.escape4RegexpFuzzy(filterTxt), 'i') : null;
		let filterAccountsExtended: Set<string>;
		if (this.filterAccounts.value.length) {
			// on étend aux groupes d'appartenance
			const users = await this.usersSrv.getUserMap(this.filterAccounts.value, ["flattenedGroups"]);
			filterAccountsExtended = new Set();
			for (const account in users) {
				filterAccountsExtended.add(account);
				(users[account]?.flattenedGroups as string[])?.forEach(grp => filterAccountsExtended.add(grp));
			}
		}

		const disabledRoles = this.grid.getAttribute("data-roles-disabled")?.split(" ").filter(entry => entry ? true : false);
		let filterRolesWhiteList: Set<string>;
		if (disabledRoles?.length) {
			filterRolesWhiteList = new Set();
			this.grid.columnDefs.forEach((col) => {
				if (col instanceof GridColDefRole) {
					// Création du filtre
					if (disabledRoles.indexOf(col.roleDef.role) == -1) filterRolesWhiteList.add(col.roleDef.role);
				}
			});
		}
		// Maj UI du style "disabled" des colonnes
		this.grid.shadowRoot.querySelectorAll("#headers > .colH[data-role]").forEach((elt) => {
			const role = elt.getAttribute("data-role");
			DOM.setAttrBool(elt, "disabled", disabledRoles ? disabledRoles.indexOf(role) != -1 : false);
		});

		const vWithFilter = (filterTxt || filterAccountsExtended?.size || filterRolesWhiteList?.size) ? true : false;
		this.grid.classList.toggle("filtered", vWithFilter);
		if (vWithFilter) {
			let filteredDatas: R[] = this.filterTreeDataHolderChiltren(JSON.parse(JSON.stringify(this._lastFetchDatas)), filterTxtPattern, filterAccountsExtended, filterRolesWhiteList);
			this.dataHolder.setDefaultOpenState(GridDataHolderJsonTree.defaultOpened as any);
			this.dataHolder.setDatas(filteredDatas);
		} else {
			this.dataHolder.setDefaultOpenState(GridDataHolderJsonTree.defaultOpened as any);
			this.dataHolder.setDatas(this._lastFetchDatas);
		}

		//Resélection des row sélectionnées avant refresh
		this.selectTreeByDatas(selectedRows, true);

		this.toolbarTop.refreshContent();
		this.razFilterBtn.refresh();
	}

	/** Réinitialisation des différentes zones de filtre */
	public razFilters(): void {
		this.filterTxt.value = this.params.filterTxt || "";
		this.filterAccounts.value = this.params.filterAccounts || [];
		this.grid.removeAttribute("data-roles-disabled");
		this.refresh();
	}

	get isFiltered(): boolean {return this.grid ? this.grid.classList.contains("filtered") : false}


	/** Reconstruction des datas du tree */
	abstract doFetchDatas(): Promise<R[]>

	/**
	 * Sélection des entrées de l'arbre via une liste de R sur un tree **chargé**
	 * Si une entrée demandée n'est pas présent dans le tree, aucune erreur n'est remontée
	 */
	selectTreeByDatas(treeDataEntries: R[], ensureRowVisible = false): void {
		if (treeDataEntries != null) {
			const selectedRows: number[] = [];
			for (let i = 0, c = this.dataHolder.countRows(); i < c; i++) {
				const row = this.dataHolder.getRow(i).rowDatas as R;
				if (row && treeDataEntries.find(entry => {
					return entry && entry.uid == row.uid;
				})) {
					selectedRows.push(i);
				}
			}
			if (selectedRows.length > 0) {
				this.grid.setSelectedRows(selectedRows);
				if (ensureRowVisible) this.grid.ensureRowVisible(selectedRows[0]);
			} else
				this.grid.clearSel();
		}
	}

	protected rolesUiHandlers(): Promise<(RoleEditUiHandler<IRoleEditUiHandlerContext> & RoleUiHandlerContextUsed)[]> {
		return Promise.all(this.reg.mergeLists(ROLES.UI_ROLES_LIST)) as Promise<RoleEditUiHandler<IRoleEditUiHandlerContext>[]>;
	}

	static isRolesUiHandlerUsableFor(roleUiHandler: RoleEditUiHandler<IRoleEditUiHandlerContext> & RoleUiHandlerContextUsed, entry: JRoleExpEntry): boolean {
		if (roleUiHandler?.whiteListLevelsUid?.length && entry.uid)
			return roleUiHandler.whiteListLevelsUid.find(white => entry.uid.startsWith(white)) ? true : false;
		return true;
	}

	/** Construction des colonnes de base */
	protected colsDefBase(): GridColDef[] {
		return [
			new GridColTreeDef('tree')
				.setFlex('20rem', 1, 1).setMinWidth('20rem')
				.setLabel("")
				.setCellBuilder(new CellBuilderMainRoleLevelEntry(this.reg, "title")),
		]
	}

	/** maj des colonnes des rôles */
	protected async updateRolesColsDef(usedRoles: Set<string>): Promise<void> {
		const colsDef = this.colsDefBase();
		let roleUiCtx: IRoleEditUiHandlerContext = {
			reg: this.reg
		}
		const rolesUiHandlers = await this.rolesUiHandlers()
		let resultRoles = await ROLES.resolveRolesList(Array.from(usedRoles), roleUiCtx, rolesUiHandlers);
		resultRoles.forEach(entry => {
			colsDef.push(new GridColDefRole(entry, rolesUiHandlers.find(roleUi => roleUi.roleCode === entry.role)));
		})
		this.grid.columnDefs = colsDef;
	}

	/** Filtre : application du filtre */
	private _applyFilterWaiter: number;

	private onKeyPress(this: HTMLInputElement, ev: KeyboardEvent) {
		if (ev.key === 'ArrowDown') {
			const tree = DOMSH.findHost(this) as RolesExplorerTabBase<E, R, CTX>;
			if (tree.grid.getSelectedRow() !== undefined) tree.grid.focus();
		}
	}

	private onApplyFilter(this: HTMLInputElement, ev: InputEvent) {
		const me = DOMSH.findHost(this) as RolesExplorerTabBase<E, R, CTX>;
		if (me._applyFilterWaiter) clearTimeout(me._applyFilterWaiter);
		me._applyFilterWaiter = setTimeout(() => me.refreshUi(false), 200) as any;
	}


	private filterTreeDataHolderChiltren(ch: R[], filterTxtPattern?: RegExp, filterAccountsExtended?: Set<string>, filterRolesWhiteList?: Set<string>): R[] {
		if (!filterTxtPattern && !filterAccountsExtended?.size && !filterRolesWhiteList?.size) return ch;
		return ch?.filter((entry) => {
			const filterTxtPatternMatch = filterTxtPattern?.test((entry as JRoleExpEntryStructLevel<R>).title);
			if ((entry as JRoleExpEntryStructLevel<R>).ch) (entry as JRoleExpEntryStructLevel<R>).ch = this.filterTreeDataHolderChiltren((entry as JRoleExpEntryStructLevel<R>).ch, filterTxtPatternMatch ? null : filterTxtPattern, filterAccountsExtended, filterRolesWhiteList);
			if ("title" in (entry as JRoleExpEntryStructLevel<R>)) {
				// - Data de type "JRoleExpEntryStructLevel"
				if (filterTxtPattern) {
					if (filterTxtPatternMatch) return true;
					// si level possède des fils de type "structLevel" : on affiche même si ne matche pas
					else if ((entry as JRoleExpEntryStructLevel<R>).ch?.find(entry => {
						return "structLevel" in (entry as JRoleExpEntryStructLevel<R>) && (entry as JRoleExpEntryStructLevel<R>).structLevel != 'roles'
					})) return true;

				} else if ((entry as JRoleExpEntryStructLevel<R>).ch?.length)
					return true;
			} else if ("account" in (entry as JRoleExpEntrySetAccount)) {
				// - Data de type "JRoleExpEntrySetAccount"
				if (!filterAccountsExtended?.size && !filterRolesWhiteList?.size) return true;
				if (filterAccountsExtended?.size && !filterAccountsExtended.has((entry as JRoleExpEntrySetAccount).account)) return false;
				if (filterRolesWhiteList?.size) {
					// si tous les roles sont masqués, on supp la ligne
					if (!((entry as JRoleExpEntrySetAccount).denied?.find(entry => filterRolesWhiteList.has(entry)) || (entry as JRoleExpEntrySetAccount).allowed?.find(entry => filterRolesWhiteList.has(entry)))) return false;
				}
				return true;
			} else
				return true;
		});
	}

	/** Grid */
	redrawLine(row: GridDataRowJson<R>, line: HTMLElement): void {
		DOM.setAttr(line, "data-struct-level", (row.rowDatas as JRoleExpEntryStructLevel<R>).structLevel);
		DOM.setAttrBool(line, "data-account", (row.rowDatas as JRoleExpEntrySetAccount).account ? true : false);
	}

	/** Details */
	private async showDetails(row: R[] | null): Promise<boolean> {
		if (row === null || row.length == 0) {
			DOM.setHidden(this._detailsBoxElt, true);
			return false;
		} else if (row.length == 1) {
			this._detailsBoxElt.innerHTML = "";
			const result = await new MsgOver().setCustomMsg("Veuillez patienter...", "info").showMsgOver(this._detailsBoxElt).waitFor(
				this.buildRowDetails(row[0])
			);
			if (this._detailsBoxElt.innerHTML != "") return; //race condition
			this._detailsBoxElt.appendChild(<Box skinOver={`webzone:page c-rolesexplorer-core-widgets rolesexplorer-core-details ${this.localName}-details`}>
				<ShadowJsx>{result}</ShadowJsx>
			</Box>);

			DOM.setHidden(this._detailsBoxElt, !result);
		}
	}

	/** Construction de la zone "détails" d'une row */
	abstract buildRowDetails(row: R): Promise<HTMLElement>

}

REG.reg.registerSkin('rolesexplorer-core', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-width: 0;
		min-height: 0;
		flex-direction: column;
		overflow: hidden;
	}

	#toolbarsBox {
		display: flex;
		flex-direction: column;
	}

	c-bar-actions.showToolbarTop {
	  flex: 1;
  }

  c-bar-actions.showToolbarTop {
	  flex-direction: column;
	  border-inline-start: 1px solid var(--border-color);
  }

  #filter {
	  background: .1em / 1em no-repeat url(/@skin@/commons/icons/filter.svg);
	  padding-block: 2px;
	  padding-inline: 1.2em 2px;
	  display: flex;
	  min-width: 0;
	  min-height: 0;
	  align-items: center;
	  overflow: hidden;
	  flex-wrap: wrap;
  }

  #filter > * {
	  margin-block: 0;
	  margin-inline: .3rem 0;
  }

  #filter > select {
	  border: none;
  }

  #filter > input {
	  min-width: 10em;
	  background-color: var(--form-search-bgcolor);
  }

  #filter > input:placeholder-shown {
	  font-style: italic;
  }

	c-input-users-panel {
	  min-width: 12rem;
	  background-color: var(--form-search-bgcolor);
  }

  #content {
	  display: flex;
	  flex: 1;
	  overflow: auto;
	  min-height: 30%
  }

  #gridContent {
	  display: flex;
	  flex: 1;
	  flex-direction: column;
  }

  c-grid {
	  flex: 1;
	  overflow: auto;
	  border: none;
  }

  c-resizer {
	  pointer-events: auto;
	  cursor: ns-resize;
	  height: 3px;
  }

  #details {
	  display: flex;
	  flex: .3;
	  flex-direction: column;
	  overflow: auto;
	  min-height: 5em;
  }

  #details[hidden] {
	  display: none;
  }

  #details > c-box {
	  flex: 1;
  }


`);

REG.reg.registerSkin('c-rolesexplorer-core-widgets', 1, /* language=CSS */ `

	.roleName {
		display: inline-flex;
		font-variant: small-caps;
		/*color:var(--alt1-color);*/
	}

	.roleName.role-unknown {
		text-decoration: line-through;
	}

	.roleName.role-inherited {
		font-style: italic;
	}

`);

REG.reg.registerSkin('rolesexplorer-core-tree', 1, /* language=CSS */ `
	:host {
		/*--roles-struct-level-roles: url('/@skin@/builder/widgets/moan-info.svg');*/
	}

	#scroll {
		/* Permet d'éviter les sauts lors des filtes*/
		overflow-y: scroll;
	}

	/** Ligne d'entete */
	.colH {
		max-height: unset;
	}

	/*.colH[data-role-group] > c-button {
		border-block-start: 2px var(--alt1-color);
		border-block-start-style: outset;
	}*/

	.colH[data-role-group] {
		flex-direction: column;
  }

  .colH[data-role-group] > .grp {
	  font-size: xx-small;
	  color: var(--inv-color);
	  overflow: hidden;
	  text-overflow: ellipsis;
	  white-space: nowrap;
	  padding: 2px;
	  font-family: sans-serif;
	  border-radius: 4px;
	  text-align: center;
	  opacity: 0.7;
  }

  .colH:not([data-role-group]) {
	  padding-block-start: 1rem;
  }

  .colH[data-role] > c-button {
	  padding-inline-end: 1rem;
	  margin-inline-start: 0.3rem;
	  background: url(/@skin@/commons/icons/checkbox-checked.svg) no-repeat right .4rem;
	  background-size: .8rem;
	  align-items: start;
	  /*
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
		*/

  }

  .colH[data-role][disabled] > c-button {
	  background-image: url(/@skin@/commons/icons/checkbox-unchecked.svg);
  }

  .cell.role[disabled],
  .colH[data-role][disabled] {
	  background-image: url(/@skin@/commons/icons/filtered.svg);
  }

  .line > .cell.role[unusedForLevel] {
	  background-color: #55555566;
	  filter: var(--inv-active-filter);
  }

  /** Ligne "Account" */
  div[data-account] > .cell {
	  padding: 0px 1px;
  }

  div[data-account] > .cell:first-child > x-twisty {
	  padding-inline-start: .5rem;
  }

  div[data-account] > .cell:first-child > span {
	  border-inline-start: 1px solid var(--border-color);
	  font-size: smaller;
	  padding-inline-start: 0.5em;
  }

  /** Ligne "Level" */
  div:not([data-struct-level='roles']) + div[data-struct-level]:not([data-struct-level='roles']) {
	  margin-block-start: .5rem;
  }

  /** Ligne "Rôles affectés" */
  div[data-struct-level='roles'] > .cell {
	  padding: 0px 1px;
  }

  div[data-struct-level='roles'] > .cell:first-child > span {
	  font-style: italic;
	  font-size: x-small;
  }

  div[data-struct-level='roles'] > .cell:first-child > x-twisty.opened {
	  background-image: url(/@skin@/commons/widgets/grid/opened.svg);
	  background-position-x: center;
  }

  div[data-struct-level='roles'] > .cell:first-child .icon {
	  padding-inline-start: unset !important;
  }

  /** Cellule de rôle */
  .role {
	  text-align: center;
  }

  .role > .icon {
	  padding-inline-start: calc(var(--icon-size) * .8) !important;
  }

  *[hidden] {
	  display: none;
  }
`);

REG.reg.registerSkin('rolesexplorer-core-details', 1, /* language=CSS */ `
	c-bar-actions {
		flex-direction: row;
		border-bottom: 1px solid var(--border-color);
	}

	/** Details */
	.header {
		padding: 0.3em;
		font-weight: bold;
	}

	.resolvedRoles {
		text-align: center;
		margin-inline: 10%;
		margin-block: 0.5em;
		border: 2px dotted var(--alt2-border-color);
	}

	c-userref {
		display: inline-flex;
		margin: 0.2em;
		padding: 0.1em;
		border: 1px dotted var(--border-color);
		border-radius: 0.4em;
		background-color: var(--alt2-bgcolor);
		color: unset;
		font-size: smaller;
	}

	c-input-users-panel {
		display: inline-flex;
	}

`);


/** CellBuilder **/

class GridColDefRole extends GridColDef {
	constructor(public roleDef: { role: string, name: string }, roleUiHandler?: RoleEditUiHandler<IRoleEditUiHandlerContext> & RoleUiHandlerContextUsed) {
		super(roleDef.role);
		this.setLabel(roleDef.name || roleDef.role);
		this.setDescription(roleDef.name || roleDef.role);
		this.setCellBuilder(new CellBuilderRole(roleDef.role, roleUiHandler));
		this.setFlex('2rem', 1, 1)
		this.setMaxWidth('15rem');
	}

	isFlagDisabledCol(root: HTMLElement): boolean {
		const grid = DOMSH.findHost<Grid>(root);
		const gridDisabledRoles = new Set(grid.getAttribute("data-roles-disabled")?.split(" "));
		return gridDisabledRoles.has(this.roleDef.role);
	}

	hasOtherFlagDisabledCol(root: HTMLElement): boolean {
		const grid = DOMSH.findHost<Grid>(root);
		const gridDisabledRoles = new Set(grid.getAttribute("data-roles-disabled")?.split(" "));
		const headerDiv = root.parentElement;
		if (Array.from(headerDiv.childNodes).find((cell: HTMLDivElement) => {
			const role = cell.getAttribute("data-role");
			if (role && role != this.roleDef.role) {
				if (gridDisabledRoles.has(role)) return true;
			}
		})) return true;
	}


	setFlagDisabledCol(root: HTMLElement, forcedDisabled: boolean | 'toogle' = 'toogle', forcedDisabledOnOtherCols?: boolean | 'toogle') {
		const grid = DOMSH.findHost<Grid>(root);
		const gridDisabledRoles = new Set(grid.getAttribute("data-roles-disabled")?.split(" "));
		const cellNewSateIsDisabled = forcedDisabled == "toogle" ? !this.isFlagDisabledCol(root) : forcedDisabled;

		if (cellNewSateIsDisabled) gridDisabledRoles.add(this.roleDef.role);
		else gridDisabledRoles.delete(this.roleDef.role);

		if (forcedDisabledOnOtherCols != null) {
			const headerDiv = root.parentElement;
			headerDiv.childNodes.forEach((cell: HTMLDivElement) => {
				const role = cell.getAttribute("data-role");
				if (role && role != this.roleDef.role) {
					if (forcedDisabledOnOtherCols == "toogle") forcedDisabledOnOtherCols = !cellNewSateIsDisabled;
					if (forcedDisabledOnOtherCols) gridDisabledRoles.add(role);
					else gridDisabledRoles.delete(role);
				}
			});

		}

		DOM.setAttr(grid, "data-roles-disabled", Array.from(gridDisabledRoles).join(" "));
	}

	buildColHeader(col: GridCol, root: HTMLElement) {
		const gridDisabledRoles = new Set(col.grid.getAttribute("data-roles-disabled")?.split(" "));

		DOM.setAttrBool(root, "disabled", gridDisabledRoles?.has(this.roleDef.role));

		col.rootHeader = root;
		DOM.setAttr(root, "data-role", this.roleDef.role);
		const roleGrp = ROLES.extractGroupFromRole(this.roleDef.role);
		let title = `Rôle : ${this.roleDef.name}`;
		if (roleGrp) {
			const grpDef = ROLES.findGroupDef(roleGrp);
			title = `Groupe exclusif : ${grpDef.label}\n${title}`;
			DOM.setAttr(root, "data-role-group", roleGrp);
			const grpElt = root.appendChild(<div class="grp">{grpDef.label ? grpDef.label : ""}</div>);
			if (grpDef.color) grpElt.style.backgroundColor = grpDef.color;
		}

		const btn = root.appendChild(
			<c-button class="colBody roleName"
								label={this.getLabel(col)}
								ui-context="custom"
								onclick={(ev: MouseEvent) => {
									if (ev.ctrlKey)
										this.setFlagDisabledCol(root, false);
									else {
										if (!this.isFlagDisabledCol(root) && this.hasOtherFlagDisabledCol(root))
											this.setFlagDisabledCol(root, false, false);
										else
											this.setFlagDisabledCol(root, false, true);
									}
									ev.stopPropagation();
								}}/>) as Button;

		root.title = title;
	}
}

class CellBuilderRole extends CellBuilderFlagIcon {

	roleRegexp: RegExp;

	constructor(protected role: string, protected roleUiHandler?: RoleEditUiHandler<IRoleEditUiHandlerContext> & RoleUiHandlerContextUsed) {
		super(role);
	}

	redrawCell(row: GridDataRowJson<JRoleExpEntrySetAccount>, root: HTMLElement): void {
		const grid = DOMSH.findHost<Grid>(root);
		super.redrawCell(row, root);
		DOM.addClass(root, "role");
		DOM.setAttrBool(root, "disabled", grid.getAttribute("data-roles-disabled")?.split(" ").indexOf(this.role) > -1);
		const isRolesUiHandlerUsable = RolesExplorerTabBase.isRolesUiHandlerUsableFor(this.roleUiHandler, row.rowDatas);
		DOM.setAttrBool(root, "unusedForLevel", !isRolesUiHandlerUsable);
		root.title = !isRolesUiHandlerUsable ? "Rôle non exploité sur ce niveau" : "";

	}

	_getValue(row: GridDataRowJson<JRoleExpEntrySetAccount>): string {
		if (this.role) {
			if (row.rowDatas.denied?.indexOf(this.role) > -1)
				return "Rôle retiré";
			if (row.rowDatas.allowed?.indexOf(this.role) > -1)
				return "Rôle affecté";
		}
		return "";
	}

	protected _getIcon(row: GridDataRowJson<JRoleExpEntrySetAccount>): string {
		if (this.role) {
			if (row.rowDatas.denied?.indexOf(this.role) > -1)
				return "/@skin@/core/dialogs/rolesExplorer/removed.svg";
			if (row.rowDatas.allowed?.indexOf(this.role) > -1)
				return "/@skin@/core/dialogs/rolesExplorer/added.svg";
			return "";
		}
	}
}

class CellBuilderMainRoleLevelEntry extends CellBuilderIconLabel {
	cacheKey: symbol;

	constructor(protected reg: IReg<ICoreUniverseEnv>, dataKey: string) {
		super(dataKey);
		this.iconWidth = "1.3rem";
	}

	protected _getIcon(row: GridDataRowJson<JRoleExpEntryStructLevel<any> | JRoleExpEntrySetAccount>): string {
		if ("structLevel" in (row.rowDatas as JRoleExpEntryStructLevel<any>))
			return `--roles-struct-level-${(row.rowDatas as JRoleExpEntryStructLevel<any>).structLevel}`
	}

	redrawCell(row: GridDataRowJson<JRoleExpEntryStructLevel<any> | JRoleExpEntrySetAccount>, root: HTMLElement): void {
		super.redrawCell(row, root);

		const span = root.firstElementChild as HTMLElement;
		let userRef: UserRef = span.nextElementSibling as UserRef;
		if ("account" in (row.rowDatas as JRoleExpEntrySetAccount)) {
			if (!userRef)
				userRef = root.appendChild(<UserRef class="inline list" î={{reg: this.reg, withIcon: true, skinOver: "rolesExplorer/userRef"} as OUserRefInit}/>) as UserRef;
			userRef.nickOrAccount = row.getData("account");
			DOM.setHidden(userRef, false);
			DOM.setHidden(span, true);
		} else {
			if (userRef) DOM.setHidden(userRef, true);
			DOM.setHidden(span, false);
		}
	}
}

REG.reg.registerSkin('rolesExplorer/userRef', 1, /* language=CSS */ `
	:host([data-withIcon]) span {
		background-image: url(/@skin@/core/dialogs/rolesExplorer/user.svg);
		background-size: auto 1rem;
	}

	:host([data-isGroup][data-withIcon]) span {
		background-image: url(/@skin@/core/dialogs/rolesExplorer/group.svg);
	}
`);

/** Actions **/

/** RAZ filtres */
const actionRazFilters = new Action<IUsersRolesActionCtx<any, any, any>>("razFilters")
	.setLabel("Réinitialiser les filtres")
	.setGroup("filters")
	.setIcon("/@skin@/commons/icons/reset-filter.svg")
	.setEnabled((ctx: IUsersRolesActionCtx<any, any, any>) => {
		return ctx.rolesExplorer.isFiltered;
	})
	.setExecute((ctx: IUsersRolesActionCtx<any, any, any>, ev: Event) => {
		ctx.rolesExplorer.razFilters();
	});

/** Reload UI */
const actionRefreshUi = new Action<IUsersRolesActionCtx<any, any, any>>("refreshUi")
	.setLabel("Rafraichir la liste des rôles")
	.setGroup("reload")
	.setIcon("/@skin@/commons/icons/refresh.svg")
	.setExecute((ctx: IUsersRolesActionCtx<any, any, any>, ev: Event) => {
		ctx.rolesExplorer.refresh();
	});

/** Wrapper d'une action qui déclenche un refresh du tree
 * FIXME : usage pas optimal... passer plutot par des remontées d'event de modif sur les objets concernés */
export class ActionWrapperRefresRolesExplorer<C extends IUsersRolesActionCtx<any, any, any>> extends ActionWrapper<C> {

	isEnabled(ctx: C): boolean {
		return super.isEnabled(ctx);
	}

	async execute(ctx: C, ev?: Event): Promise<any> {
		let result = await super.execute(ctx, ev);
		await ctx.rolesExplorer.refresh();
		return result;
	}
}

